import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { UserPlus } from "lucide-react";
import type { UsersResponse } from "@mubin/contracts";
import { displayPhone } from "@mubin/contracts";
import { Badge, Empty, Loading, PageTitle, Tabs } from "../components/ui";
import { CreateUserModal } from "../components/user-forms";
import { FormerMembers } from "../components/former-members";
import { api, errorText } from "../lib/api";
import { personGenderLabel, roleLabel, userStatusLabel } from "../lib/labels";
import { useCan } from "../lib/org";
import { useMe } from "../lib/session";

export type UsersTab = "student" | "teacher" | "staff" | "former";
const STAFF = ["supervisor", "branch_manager", "org_admin"] as const;

export function UsersPage() {
  const { data: me } = useMe();
  const can = useCan();
  const search = useSearch({ from: "/app/users" });
  const navigate = useNavigate({ from: "/users" });
  const tab: UsersTab = search.tab ?? "student";
  const [q, setQ] = useState(search.q ?? "");
  const [creating, setCreating] = useState(false);
  const params = new URLSearchParams();
  if (tab !== "staff" && tab !== "former") params.set("role", tab);
  if (search.q) params.set("q", search.q);
  if (search.status) params.set("status", search.status);
  const users = useQuery({
    queryKey: ["users", me?.activeRole, "list", tab, search.q ?? "", search.status ?? ""],
    queryFn: () => api<UsersResponse>(`/v1/users?${params}`),
    enabled: tab !== "former",
  });
  const rows = (users.data?.users ?? []).filter(
    (u) => tab !== "staff" || u.roles.some((r) => (STAFF as readonly string[]).includes(r.role)),
  );

  return (
    <>
      <PageTitle
        title="المستخدمون"
        sub="الحسابات ضمن نطاقك. الطلاب يسجّلون من التطبيق، أو تضيفهم الإدارة هنا بخطتهم."
        actions={
          can.has("users.manage") && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <UserPlus size={18} strokeWidth={1.75} /> حساب جديد
            </button>
          )
        }
      />
      <section className="card">
        <div className="toolbar">
          <Tabs
            label="نوع الحساب"
            value={tab}
            onChange={(t) => void navigate({ search: { ...search, tab: t } })}
            items={[
              { value: "student", label: "الطلاب" },
              { value: "teacher", label: "المعلمون" },
              { value: "staff", label: "الإشراف والإدارة" },
              { value: "former", label: "سُحبت أدوارهم" },
            ]}
          />
          {tab === "former" ? null : (
            <>
              <form
                style={{ display: "contents" }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void navigate({ search: { ...search, q: q.trim() || undefined } });
                }}
              >
                <input
                  type="search"
                  aria-label="بحث"
                  placeholder="بحث بالاسم أو اسم المستخدم أو الجوال"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </form>
              <select
                aria-label="الحالة"
                value={search.status ?? ""}
                onChange={(e) =>
                  void navigate({
                    search: {
                      ...search,
                      status: (e.target.value || undefined) as "active" | "suspended" | undefined,
                    },
                  })
                }
              >
                <option value="">كل الحالات</option>
                <option value="active">نشط</option>
                <option value="suspended">موقوف</option>
              </select>
            </>
          )}
        </div>
        {tab === "former" ? (
          <FormerMembers />
        ) : users.isPending ? (
          <Loading />
        ) : users.isError ? (
          <Empty title="تعذر التحميل">{errorText(users.error)}</Empty>
        ) : rows.length === 0 ? (
          <Empty title="لا توجد نتائج" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>الدخول</th>
                  <th>{tab === "student" ? "الحلقة" : "الأدوار"}</th>
                  <th>الجنس</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const enrollment = u.enrollments.find((e) => e.status === "active");
                  return (
                    <tr key={u.id}>
                      <td>
                        <Link to="/users/$userId" params={{ userId: u.id }}>
                          {u.name}
                        </Link>
                      </td>
                      <td>
                        <span className="ltr muted small">{displayPhone(u.phone) || "—"}</span>
                      </td>
                      <td>
                        {tab === "student" ? (
                          enrollment ? (
                            <>
                              {enrollment.circleName}{" "}
                              <span className="muted small">· {enrollment.branchName}</span>
                            </>
                          ) : (
                            <Badge tone="warning">غير مسجل في حلقة</Badge>
                          )
                        ) : (
                          <div className="chips">
                            {u.roles.map((r) => (
                              <Badge key={r.assignmentId} tone="brand">
                                {roleLabel[r.role]}
                                {r.scopeName ? ` · ${r.scopeName}` : ""}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>{u.gender ? personGenderLabel[u.gender] : <span className="muted">—</span>}</td>
                      <td>
                        <Badge tone={u.status === "active" ? "success" : "danger"}>
                          {userStatusLabel[u.status]}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length >= 200 && (
              <p className="muted small section">تظهر أول 200 نتيجة؛ استخدم البحث للتضييق.</p>
            )}
          </div>
        )}
      </section>
      <CreateUserModal
        open={creating}
        onClose={() => setCreating(false)}
        role={tab === "student" ? "student" : tab === "teacher" ? "teacher" : undefined}
        staffOnly={tab === "staff"}
      />
    </>
  );
}
